import { Plus, RefreshCw, Search } from "lucide-react";

import { Input } from "@/components/ui/input";

import { Button } from "@/components/ui/button";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  NetworkType,
  type BlockchainNetworkFilters,
} from "@/interfaces/blockchainNetwork";

interface Props {
  filters: BlockchainNetworkFilters;
  loading?: boolean;
  onFiltersChange: (filters: BlockchainNetworkFilters) => void;
  onRefresh: () => void;
  onCreate: () => void;
  onReset: () => void;
}

const BlockchainNetworkToolbar = ({
  filters,
  loading,
  onFiltersChange,
  onRefresh,
  onCreate,
  onReset,
}: Props) => {
  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="grid gap-4 xl:grid-cols-6">
        <div className="xl:col-span-2 relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />

          <Input
            className="pl-9"
            placeholder="Search network..."
            value={filters.search}
            onChange={(e) =>
              onFiltersChange({
                ...filters,
                search: e.target.value,
              })
            }
          />
        </div>

        <Select
          value={filters.enabled}
          onValueChange={(value) =>
            onFiltersChange({
              ...filters,
              enabled: value as BlockchainNetworkFilters["enabled"],
            })
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Status" />
          </SelectTrigger>

          <SelectContent>
            <SelectItem value="ALL">All Status</SelectItem>

            <SelectItem value="ACTIVE">Enabled</SelectItem>

            <SelectItem value="DISABLED">Disabled</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={filters.networkType}
          onValueChange={(value) =>
            onFiltersChange({
              ...filters,
              networkType: value as BlockchainNetworkFilters["networkType"],
            })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>

          <SelectContent>
            <SelectItem value="ALL">All Networks</SelectItem>

            <SelectItem value={NetworkType.MAINNET}>Mainnet</SelectItem>

            <SelectItem value={NetworkType.TESTNET}>Testnet</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={filters.architecture}
          onValueChange={(value) =>
            onFiltersChange({
              ...filters,
              architecture: value as BlockchainNetworkFilters["architecture"],
            })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>

          <SelectContent>
            <SelectItem value="ALL">All Architectures</SelectItem>

            <SelectItem value="EVM">EVM</SelectItem>

            <SelectItem value="UTXO">UTXO</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={filters.sortBy}
          onValueChange={(value) =>
            onFiltersChange({
              ...filters,
              sortBy: value as BlockchainNetworkFilters["sortBy"],
            })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>

          <SelectContent>
            <SelectItem value="priority">Priority</SelectItem>

            <SelectItem value="name">Name</SelectItem>

            <SelectItem value="createdAt">Created Date</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-5 flex justify-between">
        <Button variant="outline" disabled={loading} onClick={onRefresh}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
        <Button variant="ghost" onClick={onReset}>
          Reset Filters
        </Button>

        <Button onClick={onCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Add Network
        </Button>
      </div>
    </div>
  );
};

export default BlockchainNetworkToolbar;
